import tenantConstants from '@constants';
import tenantMenuList from '@menuList';
import tenantTheme from '@theme';
import { Badge, Menu } from 'antd';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { withTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Button, Flex, Icon, Tag } from '../components/common';
import { LangAr, LangEn } from '../components/svg';
import { useGetLocation, useRouteNavigate } from '../hooks';
import { helpCenterClickEvent } from '../services/analyticsService/index.js';
import TenantComponents from '@components';
import { getAppLanguage } from '../utility/language.js';
import { useLazyGetUnreadCountQuery } from '../apis/common.js';

const langIcons = { en: LangEn, ar: LangAr };

const MenuItems = ({ t, toggleCollapsed, handleTopBarSticky, collapsed }) => {
  const navigate = useRouteNavigate();
  const pathName = window.location.pathname;
  const { user } = useSelector((state) => state.app.loginUser);
  const sideBarList = tenantMenuList.menuList(user?.permissions, user?.agency, user?.type !== 'free');
  const [selectedKeys, setSelectedKeys] = useState([]);
  const { isMobile, darkMode, topMenu, isMemberArea, locale } = useSelector((state) => state.app.AppConfig);
  const [currentClickedItem, setCurrentClickedItem] = useState(null);
  const currentPath = useGetLocation();
  const sidebarRef = useRef(null);
  const helpSupportModalRef = useRef();

  const [getUnreadCounts, { data: badgeValues }] = useLazyGetUnreadCountQuery();
  const isClickToggleSidebarEnabled = tenantConstants.PUSH_CONTENT_ON_SIDEBAR_EXPAND && !isMobile;

  useEffect(() => {
    if (user) {
      getUnreadCounts();
    }
  }, [user]);

  useEffect(() => {
    const keys = [];
    sideBarList.forEach((e) => {
      if ((e.path && pathName.includes(e.path)) || pathName.includes(e?.basePath)) {
        keys.push(e.key);
        handleTopBarSticky(e.hideTopBar);
      } else if (e.list) {
        e.list.forEach((it) => {
          if ((it.path && pathName.includes(it.path)) || pathName.includes(it?.basePath)) {
            keys.push(it.key);
            handleTopBarSticky(it.hideTopBar);
          }
        });
      }
    });
    setSelectedKeys(keys);
  }, [window.location.pathname]);

  // useEffect(() => {
  //   const handleClickOutside = event => {
  //     if (sidebarRef.current && !sidebarRef.current.contains(event.target)) {
  //       toggleCollapsed();
  //     }
  //   };
  //   document.addEventListener('mousedown', handleClickOutside);
  //   return () => {
  //     document.removeEventListener('mousedown', handleClickOutside);
  //   };
  // }, [toggleCollapsed]);

  /** SubMenu titles do not receive Menu `onClick` while collapsed — expose parent icons as leaf items instead. */
  const flattenCollapsedParents = Boolean(isClickToggleSidebarEnabled && collapsed);

  /** Shared "NEW" tag so top-level and sub-menu items (and the bundle banner) stay visually identical. */
  const renderNewTag = () => (
    <Tag
      color={tenantTheme['danger-color']}
      style={{
        '--tag-color': '#fff',
        '--tag-font-size': '10px',
        borderRadius: '16px',
        fontWeight: 600,
        letterSpacing: '0.412px',
        flex: 'none',
      }}
    >
      {t('NEW')}
    </Tag>
  );

  /**
   * The tag trails the label text with a fixed gap instead of being pinned to the
   * item's end, so widening the sidebar never pushes it away from its option.
   */
  const renderMenuLabel = (label, showNewTag) =>
    showNewTag ? (
      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, maxWidth: '100%' }}>
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</span>
        {renderNewTag()}
      </div>
    ) : (
      <div>{label}</div>
    );

  const menuItems = useMemo(
    () =>
      sideBarList.map(({ key, list, title, icon, id, isNew }) => {
        const parentIcon =
          !!icon &&
          (badgeValues?.[key] ? (
            <Badge style={{ padding: '0px 4px', lineHeight: 'normal' }} count={badgeValues?.[key]} size="small">
              <Icon icon={icon} />
            </Badge>
          ) : (
            <Icon icon={icon} />
          ));

        if (list && flattenCollapsedParents) {
          return {
            id,
            key,
            icon: parentIcon,
            label: <div>{title}</div>,
            title: '',
          };
        }

        if (list) {
          return {
            id,
            key,
            icon: parentIcon,
            children: list.map((subItem) => ({
              key: subItem.key,
              icon: !!subItem.icon && <Icon icon={subItem.icon} />,
              label: renderMenuLabel(subItem.title, subItem.isNew),
              title: isClickToggleSidebarEnabled && collapsed ? '' : undefined,
            })),
            label: title,
            title: isClickToggleSidebarEnabled && collapsed ? '' : undefined,
          };
        }

        return {
          key,
          icon: !!icon && <Icon icon={icon} />,
          label: renderMenuLabel(title, isNew && !collapsed),
          title: isClickToggleSidebarEnabled && collapsed ? '' : undefined,
        };
      }),
    [sideBarList, badgeValues, isClickToggleSidebarEnabled, collapsed],
  );

  const onChangeLanguage = (newKey) => {
    const lang = tenantConstants.LANGUAGES?.find((e) => e.key == newKey);
    const currentLanguage = getAppLanguage();
    const pathDirectories = window.location.pathname.split('/');
    let newPath;
    if (pathDirectories[1] !== lang.key) {
      if (window.location.pathname.includes(`/${currentLanguage?.key}`)) {
        newPath = [window.location.pathname.replace(`/${currentLanguage?.key}`, `/${lang?.key}`)];
      } else {
        newPath = [pathDirectories[0], lang?.key, ...pathDirectories.slice(1)];
      }
    }
    window.location.pathname = newPath.join('/');
  };

  const onClickMenuItem = (e) => {
    let item;
    sideBarList.some((it) => {
      if (e.key === it.key) {
        item = it;
        return true;
      }
      if (it.list) {
        const child = it.list.find((el) => e.key === el.key);
        if (child) {
          item = child;
          return true;
        }
      }
      return false;
    });

    const resolvedNavItem =
      isClickToggleSidebarEnabled && collapsed && item?.list?.length ? item.list[0] : item;

    if (resolvedNavItem) {
      setCurrentClickedItem(resolvedNavItem);
      resolvedNavItem?.onClick?.();
      if (resolvedNavItem?.component) {
        sidebarRef?.current?.show?.();
        toggleCollapsed();
      } else if (resolvedNavItem.path) {
        navigate(resolvedNavItem.path);
        toggleCollapsed();
      }
    }
  };

  const languageObjectToSwitch = useMemo(
    () => tenantConstants.LANGUAGES?.length > 1 && tenantConstants.LANGUAGES?.find((e) => e.alternate === locale),
    [locale],
  );
  const LANG_ICON = langIcons[languageObjectToSwitch.key];

  const renderDynamicComponent = useCallback(() => {
    const Component = TenantComponents?.[currentClickedItem?.component];
    return (
      Component &&
      !currentPath?.pathname.includes(currentClickedItem?.path) && (
        <Component
          //  ref={sidebarRef}
          defaultState={true}
          isFetch={isMobile}
        />
      )
    );
  }, [currentClickedItem]);

  return (
    <>
      <Menu
        mode={!topMenu || isMobile ? 'inline' : 'horizontal'}
        theme={darkMode && 'dark'}
        defaultSelectedKeys={selectedKeys}
        onClick={onClickMenuItem}
        triggerSubMenuAction={isClickToggleSidebarEnabled ? 'click' : 'hover'}
        overflowedIndicator={<Icon icon="FiMoreVertical" />}
        selectedKeys={selectedKeys}
        items={menuItems}
      />
      <Flex vertical gap="8px">
        {!collapsed && <div className="text-center">{isMobile && <TenantComponents.HeaderLink />}</div>}
        {tenantConstants.SHOW_HELP_AND_SUPPORT && !isMemberArea && (
          <div className={collapsed ? 'px-8' : 'px-16'}>
            <Button
              onClick={() => {
                helpSupportModalRef.current.show(true);
                helpCenterClickEvent(user);
              }}
              outlined
              style={{
                '--btn-bg-color': tenantTheme['primary-light'],
                '--btn-content-color': tenantTheme['primary-color'],
              }}
              block
              icon="SupportIcon"
            >
              {!collapsed && t('Help & Support')}
            </Button>

            <TenantComponents.HelpAndSupport ref={helpSupportModalRef} />
          </div>
        )}
        {languageObjectToSwitch && (
          <div className={collapsed ? 'px-8' : 'px-16'}>
            <Button
              onClick={() => onChangeLanguage(languageObjectToSwitch.key)}
              outlined
              style={{
                '--btn-bg-color': tenantTheme['primary-light'],
                '--btn-content-color': tenantTheme['primary-color'],
                '--ant-button-default-hover-bg': tenantTheme['primary-light'],
              }}
              block
            >
              <>
                <LANG_ICON size="1.4em" />{' '}
                {collapsed ? languageObjectToSwitch.labelShort : languageObjectToSwitch.label}
              </>
            </Button>
          </div>
        )}
      </Flex>

      {renderDynamicComponent()}
    </>
  );
};

export default withTranslation()(MenuItems);
